// src/utils/bloodResultUtils.ts
import { BloodTestResult } from "../../types/lab/BloodTestTypes";
import { deriveFlag, resolveRange, DerivedFlag, RangeContext, RangeSource } from "./referenceRange";

export type ResultSeverity = 'NORMAL' | 'MILD' | 'MODERATE' | 'SEVERE' | 'CRITICAL';

/**
 * Fraction of the normal range used to extend the range-bar window beyond the
 * reference bounds on each side. Shared by both the badge's dot-position
 * computation and the bar's zone boundaries so they always agree.
 *
 * Must be strictly greater than the moderate→severe deviation threshold (0.30)
 * so the severe (red) band has room to render out to the edge of the bar.
 */
export const RANGE_BAR_EXTENSION = 0.5;

export interface ResultSeverityData {
	severity: ResultSeverity;
	color: string;
	lightColor: string;
	label: string;
	tagColor: 'success' | 'warning' | 'error';
	position: number;
	isAbnormal: boolean;
	deviationPercent: number;
	flag: DerivedFlag;
	source: RangeSource;
}

// Single source of truth for badge + range bar; bounds are RR-1's, null when nothing resolves
export interface ResolvedResultRange {
	numericValue: number;
	minValue: number | null;
	maxValue: number | null;
	criticalMin: number | null;
	criticalMax: number | null;
	source: RangeSource;
	display: string | null;
}

export const parseResultValue = (result: Pick<BloodTestResult, "numeric_value" | "result_value">): number => {
	if (result.numeric_value !== null && result.numeric_value !== undefined && !isNaN(parseFloat(String(result.numeric_value)))) {
		return parseFloat(String(result.numeric_value));
	}

	const resultStr = (result.result_value ?? "").trim();
	if (resultStr.startsWith('<=') || resultStr.startsWith('≤')) {
		return parseFloat(resultStr.replace(/^(<=|≤)\s*/, ''));
	}
	if (resultStr.startsWith('<')) {
		return parseFloat(resultStr.replace(/^<\s*/, '')) * 0.9;
	}
	if (resultStr.startsWith('>=') || resultStr.startsWith('≥')) {
		return parseFloat(resultStr.replace(/^(>=|≥)\s*/, ''));
	}
	if (resultStr.startsWith('>')) {
		return parseFloat(resultStr.replace(/^>\s*/, '')) * 1.1;
	}
	return parseFloat(resultStr);
};

export const resolveResultRange = (result: BloodTestResult, ctx: RangeContext): ResolvedResultRange => {
	const numericValue = parseResultValue(result);
	const range = resolveRange(result, ctx);

	return {
		numericValue,
		minValue: range.min,
		maxValue: range.max,
		criticalMin: range.criticalMin,
		criticalMax: range.criticalMax,
		source: range.source,
		display: range.display,
	};
};

export const calculateResultSeverity = (result: BloodTestResult, ctx: RangeContext): ResultSeverityData => {
	const { numericValue, minValue, maxValue, criticalMin, criticalMax, source } = resolveResultRange(result, ctx);
	// Derived from the resolved band only — the lab's printed flag never drives severity
	const flag = deriveFlag(isNaN(numericValue) ? null : numericValue, { min: minValue, max: maxValue, criticalMin, criticalMax });

	if (isNaN(numericValue) || (minValue === null && maxValue === null)) {
		return {
			severity: 'NORMAL',
			color: '#52c41a',
			lightColor: '#73d13d',
			label: 'Normal',
			tagColor: 'success',
			position: 50,
			isAbnormal: false,
			deviationPercent: 0,
			flag: null,
			source,
		};
	}

	let deviationPercent = 0;
	let isHigh = false;
	let isLow = false;
	let isOutsideRange = false;

	if (maxValue !== null && numericValue > maxValue) {
		isHigh = true;
		isOutsideRange = true;
		const range = maxValue - (minValue || 0);
		deviationPercent = range > 0 ? ((numericValue - maxValue) / range) * 100 : 0;
	} else if (minValue !== null && numericValue < minValue) {
		isLow = true;
		isOutsideRange = true;
		const range = (maxValue || minValue * 2) - minValue;
		deviationPercent = range > 0 ? ((minValue - numericValue) / range) * 100 : 0;
	}

	let severity: ResultSeverity = 'NORMAL';
	let color = '#52c41a';
	let lightColor = '#73d13d';
	let label = 'Normal';
	let tagColor: 'success' | 'warning' | 'error' = 'success';

	if (flag === 'C') {
		severity = 'CRITICAL';
		color = '#ff4d4f';
		lightColor = '#ff7875';
		label = 'Critical';
		tagColor = 'error';
	} else if (isOutsideRange) {
		if (deviationPercent > 30) {
			severity = 'SEVERE';
			color = '#ff4d4f';
			lightColor = '#ff7875';
			label = isHigh ? 'Very High' : 'Very Low';
			tagColor = 'error';
		} else if (deviationPercent > 15) {
			severity = 'MODERATE';
			color = '#ff7a45';
			lightColor = '#ff9c6e';
			label = isHigh ? 'High' : 'Low';
			tagColor = 'warning';
		} else {
			severity = 'MILD';
			color = '#faad14';
			lightColor = '#ffc53d';
			label = isHigh ? 'Slightly High' : 'Slightly Low';
			tagColor = 'warning';
		}
	}

	const normalRange = (maxValue || 0) - (minValue || 0);
	const extendedMin = (minValue || 0) - normalRange * RANGE_BAR_EXTENSION;
	const extendedMax = (maxValue || 0) + normalRange * RANGE_BAR_EXTENSION;
	const totalRange = extendedMax - extendedMin;
	let position = totalRange > 0 ? ((numericValue - extendedMin) / totalRange) * 100 : 50;
	position = Math.max(0, Math.min(100, position));

	return {
		severity,
		color,
		lightColor,
		label,
		tagColor,
		position,
		isAbnormal: severity !== 'NORMAL',
		deviationPercent,
		flag,
		source,
	};
};